import { ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'

export function useMap (options) {
  const {
    showLoaderFn
  } = options || {}

  let leafletMap = null
  let fetchedMarkers = []
  // Total marker count across every location - used by useTable.js to decide
  // whether checkboxes (the way to reveal a location's marker) are worth
  // showing, independent of whatever subset ends up checked at any moment.
  const totalMarkersCount = ref(0)

  const onMapLoad = (event) => {
    const { map } = event || {}
    if (!map) {
      return
    }

    leafletMap = map
    fetchMarkers()
  }

  // Fetches (and caches) every location's marker data WITHOUT rendering
  // anything on the map - the map starts empty, markers only appear once a
  // row's checkbox is checked (see `showMarkers`). The one exception is a
  // single-marker dataset: with only one marker there's nothing meaningful to
  // pick between, so useTable.js hides the checkboxes and this shows that lone
  // marker automatically instead of leaving the map empty forever.
  const fetchMarkers = async () => {
    try {
      showLoaderFn(true)
      const { data } = await api('road-maintenance').get('work-order-geolocations/icons')

      if (!isArray(data)) {
        return
      }

      fetchedMarkers = data.filter(marker => marker?.id)
      totalMarkersCount.value = fetchedMarkers.length

      if (fetchedMarkers.length === 1) {
        renderMarkers(fetchedMarkers)
      }
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      showLoaderFn(false)
    }
  }

  const renderMarkers = (markers) => {
    if (!markers.length) {
      // `generateMarkers` only clears the existing marker group when it's given
      // a non-empty list (see MapBase.vue) - an empty selection has to clear it
      // explicitly instead.
      leafletMap?.removeMarkers()
      return
    }

    leafletMap?.onMapReady(function (map) {
      map.generateMarkers({
        markers,
        markerClusterOptions: {
          maxClusterRadius: 40
        },
        fitMarkers: true,
        fitBoundsOptions: {
          pad: 0.5
        }
      })
    })
  }

  const showMarkers = (event) => {
    const { data } = event || {}
    if (!leafletMap) {
      return
    }

    // collect ids from the checked rows
    const selectedIds = (isArray(data) ? data : []).map(obj => obj?.id).filter(Boolean)

    // render only the markers belonging to the checked rows
    const selectedMarkers = fetchedMarkers.filter(marker => selectedIds.includes(marker?.id))

    renderMarkers(selectedMarkers)
  }

  return {
    onMapLoad,
    showMarkers,
    fetchMarkers,
    totalMarkersCount
  }
}
